import { createContext, useContext, useState, useEffect, useCallback } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
} from "firebase/auth";
import { auth } from "@/lib/firebase";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);         // Firebase user object
  const [profile, setProfile] = useState(null);   // Backend profile (incl. subscription_status)
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (firebaseUser) => {
    if (!firebaseUser) { setProfile(null); return; }
    try {
      const token = await firebaseUser.getIdToken();
      const res = await fetch(`${API}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setProfile(await res.json());
    } catch (e) {
      console.error("Failed to fetch user profile:", e);
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      await fetchProfile(firebaseUser);
      setLoading(false);
    });
    return unsub;
  }, [fetchProfile]);

  const login = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const signup = async (email, password, displayName) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) await updateProfile(cred.user, { displayName });
    return cred;
  };

  const loginWithGoogle = () =>
    signInWithPopup(auth, new GoogleAuthProvider());

  const logout = () => signOut(auth);

  const getToken = async () => {
    if (!user) return null;
    return user.getIdToken();
  };

  const refreshProfile = () => fetchProfile(user);

  const isPremium = profile?.subscription_status === "premium";

  return (
    <AuthContext.Provider value={{
      user, profile, loading, isPremium,
      login, signup, loginWithGoogle, logout,
      getToken, refreshProfile,
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
